/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
//
//  SDKToolsJSONElementTransformer.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { TokenType } from "../../../model/enums/SDKTokenType";
import { UnreachableCaseError } from "../../../utils/UnreachableCaseError";
import { JSONBuilderNamingOption, } from "../SDKToolsJSONBuilder";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Token transformer that allow generic transform of tokens to different destinations, like Style Dictionary or Figma Tokens representation */
export class TokenJSONElementTransformer {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Representation
    /** Represent a singular token as SD object */
    representToken(token, allTokens, allGroups, options) {
        switch (token.tokenType) {
            case TokenType.color:
                return this.representColorToken(token, allTokens, allGroups, options);
            case TokenType.border:
                return this.representBorderToken(token, allTokens, allGroups, options);
            case TokenType.gradient:
                return this.representGradientToken(token, allTokens, allGroups, options);
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                return this.representDimensionToken(token, allTokens, allGroups, options);
            case TokenType.shadow:
                return this.representShadowToken(token, allTokens, allGroups, options);
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                return this.representStringToken(token, allTokens, allGroups, options);
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                return this.representOptionToken(token, allTokens, allGroups, options);
            case TokenType.typography:
                return this.representTypographyToken(token, allTokens, allGroups, options);
            // TODO: Should we throw an Error here or return blur and generic?
            case TokenType.blur:
                // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'object... Remove this comment to see the full error message
                return undefined;
            default:
                throw new UnreachableCaseError(token.tokenType);
        }
    }
    /** Represent full color token, including wrapping meta-information such as user description */
    representColorToken(token, allTokens, allGroups, options) {
        const value = this.representColorTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full border token, including wrapping meta-information such as user description */
    representBorderToken(token, allTokens, allGroups, options) {
        const value = this.representBorderTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full gradient token, including wrapping meta-information such as user description */
    representGradientToken(token, allTokens, allGroups, options) {
        const value = this.representGradientTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full dimension token, including wrapping meta-information such as user description */
    representDimensionToken(token, allTokens, allGroups, options) {
        const value = this.representDimensionTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full shadow token, including wrapping meta-information such as user description */
    representShadowToken(token, allTokens, allGroups, options) {
        const value = this.representShadowTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full text token, including wrapping meta-information such as user description */
    representStringToken(token, allTokens, allGroups, options) {
        const value = this.representStringTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    representOptionToken(token, allTokens, allGroups, options) {
        const value = this.representOptionTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    /** Represent full typography token, including wrapping meta-information such as user description */
    representTypographyToken(token, allTokens, allGroups, options) {
        const value = this.representTypographyTokenValue(token.value, allTokens, allGroups, options);
        return this.tokenWrapper(token, value, options, allGroups);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Value Representation
    /** Represent color token value either as reference or as plain representation */
    representColorTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent dimension token value either as reference or as plain representation */
    representDimensionTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent text token value either as reference or as plain representation */
    representStringTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    representOptionTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent typography token value either as reference or as plain representation */
    representTypographyTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent border token value either as reference or as plain representation */
    representBorderTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent shadow token value either as reference or as plain representation */
    representShadowTokenValue(values, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    /** Represent gradient token value either as reference or as plain representation */
    representGradientTokenValue(value, allTokens, allGroups, options) {
        throw new Error("Not implemented");
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Object wrappers
    /** Retrieve wrapper to certain token (referenced by name) pointing to token value */
    referenceWrapper(reference, options) {
        throw new Error("Not implemented");
    }
    /** Retrieve token wrapper containing its metadata and value information (used as container for each defined token) */
    tokenWrapper(token, value, options, groups) {
        const data = {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value,
            type: undefined,
            brandId: undefined,
            comment: undefined,
        };
        if (options.includeType) {
            // @ts-expect-error TS(2322): Type '"border" | "color" | "gradient" | "measure" ... Remove this comment to see the full error message
            data.type = this.typeLabel(token.tokenType);
        }
        if (options.includeBrandId) {
            // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'undefined... Remove this comment to see the full error message
            data.brandId = token.brandId;
        }
        if (options.includeComments) {
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            data.comment =
                token.description.length > 0 ? token.description : undefined;
        }
        return data;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Naming
    /** Create full reference name representing token. Such name can, for example, look like: [g1].[g2].[g3].[g4].[token-name] */
    referenceName(referenceId, allTokens, allGroups, options) {
        const referencedToken = allTokens.get(referenceId);
        if (!referencedToken) {
            throw new Error(`JS: Unable to find token with id: ${referenceId}`);
        }
        // Find the group to which token belongs. This is really suboptimal and should be solved by the SDK to just provide the group reference
        const parentGroup = allGroups.find((g) => referencedToken.parentGroupId === g.id);
        if (!parentGroup) {
            throw new Error(`JS: Unable to find group with id: ${referenceId}`);
        }
        const tokenPart = this.safeTokenName(referencedToken, options.naming);
        const groupParts = this.referenceGroupChain(parentGroup, allGroups).map((g) => this.safeGroupName(g));
        if (!options.includeRootTypeNodes) {
            groupParts.splice(0, 1);
        }
        return [...groupParts, tokenPart].join(".");
    }
    /** Retrieve safe token name made out of normal token name
     * This replace spaces with dashes, also change anything non-alphanumeric char to it as well.
     * For example, ST&RK Industries will be changed to st-rk-industries
     */
    safeTokenName(token, naming) {
        // TODO: Naming
        const { name } = token;
        return this.processedName(name, naming);
    }
    /** Retrieve safe group name made out of normal group name
     * This replace spaces with dashes, also change anything non-alphanumeric char to it as well.
     * For example, ST&RK Industries will be changed to st-rk-industries
     */
    safeGroupName(group) {
        // TODO: Naming
        return group.name.replace(/\W+/g, "-").toLowerCase();
    }
    processedName(name, naming) {
        switch (naming) {
            case JSONBuilderNamingOption.camelcase:
                return (name
                    // eslint-disable-next-line func-names
                    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
                    return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                    .replace(/\s+/g, ""));
            case JSONBuilderNamingOption.kebabcase:
                return name.replace(/\W+/g, "-").toLowerCase();
            case JSONBuilderNamingOption.original:
                return name;
            case JSONBuilderNamingOption.snakecase:
                // @ts-expect-error TS(2531): Object is possibly 'null'.
                return name
                    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
                    .map((x) => x.toLowerCase())
                    .join("_");
            default:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown naming option: ${naming}`);
        }
    }
    /** Retrieve human-readable token type in unified fashion, used both as token type and as token master group */
    // TODO: Should we return specific type?
    typeLabel(type) {
        switch (type) {
            case TokenType.border:
                return "border";
            case TokenType.color:
                return "color";
            case TokenType.gradient:
                return "gradient";
            // TODO: We keep 'measure' in this file, correct?
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                return "measure";
            case TokenType.shadow:
                return "shadow";
            // TODO: Should it still be text for all of them?
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                return "text";
            // TODO: Check
            case TokenType.textCase:
                return "textCase";
            case TokenType.textDecoration:
                return "textDecoration";
            case TokenType.visibility:
                return "visibility";
            case TokenType.typography:
                return "typography";
            // TODO: Should we throw an Error here or return blur and generic?
            case TokenType.blur:
                return undefined;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    /** Retrieve chain of groups up to a specified group, ordered from parent to children */
    referenceGroupChain(containingGroup, allGroups) {
        let iteratedGroup = containingGroup;
        const chain = [containingGroup];
        while (iteratedGroup.parentGroupId) {
            const parentId = iteratedGroup.parentGroupId;
            const parentGroup = allGroups.find((g) => g.id === parentId);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            chain.push(parentGroup);
            // @ts-expect-error TS(2322): Type 'TokenGroup | undefined' is not assignable to... Remove this comment to see the full error message
            iteratedGroup = parentGroup;
        }
        return chain.reverse();
    }
}
